import {useClientFetch} from '~/composables/useClientFetch.js'
import {useCartStore} from '~/store/cartStore.js'
import {parseChatCommands, formatCartCommand} from '~/utils/chatCommands.js'

const TOKEN_COOKIE = 'hc_chat_token'
const TOKEN_COOKIE_OPTIONS = {
    path: '/',
    maxAge: 60 * 60 * 24 * 365,
    sameSite: 'lax',
}

let visitorSocket = null
let joinedToken = null
let messageListener = null
let boundEvent = null
let awaitingTimer = null
let pollTimer = null
let loadIoPromise = null

export function useChat() {
    const {t} = useI18n()
    const config = useRuntimeConfig()
    const isOpen = useState('hc-chat-open', () => false)
    const unreadCount = useState('hc-chat-unread', () => 0)
    const messages = useState('hc-chat-messages', () => [])
    const loaded = useState('hc-chat-loaded', () => false)
    const sending = useState('hc-chat-sending', () => false)
    const awaitingReply = useState('hc-chat-awaiting', () => false)
    const tokenCookie = useCookie(TOKEN_COOKIE, TOKEN_COOKIE_OPTIONS)

    function formatTime(date) {
        return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
    }

    function cartCommandStorageKey() {
        return 'hc_chat_cart_cmd_ids'
    }

    function processedCartCommandIds() {
        if (!import.meta.client) {
            return []
        }

        try {
            const raw = localStorage.getItem(cartCommandStorageKey())
            const ids = JSON.parse(raw || '[]')
            return Array.isArray(ids) ? ids.map(String) : []
        } catch {
            return []
        }
    }

    function markCartCommandProcessed(id) {
        if (!import.meta.client || id === undefined || id === null) {
            return
        }

        const ids = processedCartCommandIds().filter((value) => value !== String(id))
        ids.push(String(id))
        localStorage.setItem(cartCommandStorageKey(), JSON.stringify(ids.slice(-200)))
    }

    function wasCartCommandProcessed(id) {
        return processedCartCommandIds().includes(String(id))
    }

    function mapMessage(message) {
        const type = message.type || 'message'
        const parsed = (message.from_us || type === 'system')
            ? parseAgentCartCommands(message)
            : {text: message.text, commands: []}
        const commands = Array.isArray(parsed.commands) ? parsed.commands : []
        const commandOnly = commands.length > 0 && isCartNoticeText(parsed.text)
        const role = (type === 'system' || commandOnly)
            ? 'system'
            : (message.from_us ? 'agent' : 'user')
        let text = parsed.text

        if (commandOnly) {
            text = cartNotice(commands)
        }

        return {
            id: message.id,
            role,
            author: type === 'ai' ? '' : (message.user_name || ''),
            type,
            text,
            commands,
            time: formatTime(new Date(message.created_at)),
        }
    }

    function isCartNoticeText(text) {
        const value = String(text || '').trim()

        return value === '' || /^Added to cart:/i.test(value)
    }

    function cartNotice(commands) {
        const items = (commands || []).map(formatCartCommand).filter(Boolean).join(', ')

        return t('chat.added_to_cart', {items})
    }

    function parseAgentCartCommands(message) {
        const fromApi = Array.isArray(message.commands) ? message.commands : []
        if (fromApi.length) {
            return {text: message.text, commands: fromApi}
        }

        return parseChatCommands(message.text)
    }

    async function resolveProductId(sku) {
        const data = await useClientFetch(`product/sku/${encodeURIComponent(sku)}`)
        return data?.id || data?.product?.id || null
    }

    async function applyCartCommands(mapped) {
        if (!import.meta.client || !mapped || mapped.role === 'user') {
            return
        }

        const commands = Array.isArray(mapped.commands) ? mapped.commands : []
        if (!commands.length || wasCartCommandProcessed(mapped.id)) {
            return
        }

        const cartStore = useCartStore()
        let added = false

        for (const command of commands) {
            try {
                const id = command.id || await resolveProductId(command.sku)
                if (!id) {
                    continue
                }

                await cartStore.addItemToCart({
                    id,
                    count: command.sample ? 1 : Math.max(1, Number(command.qty) || 1),
                    sample: command.sample ? 1 : 0,
                    source: 'website_chat',
                    open: true,
                })
                added = true
            } catch {
                // Product may be unpublished; keep the chat reply visible.
            }
        }

        if (added) {
            markCartCommandProcessed(mapped.id)
        }
    }

    function greetingMessage() {
        return {
            id: 'greeting',
            role: 'agent',
            text: t('chat.greeting'),
            time: formatTime(new Date()),
        }
    }

    function setToken(token) {
        if (token) {
            tokenCookie.value = token
        }
    }

    function socketUrl() {
        if (config.public.SOCKET_URL) {
            return config.public.SOCKET_URL
        }

        if (import.meta.client) {
            const host = window.location.hostname
            if (host === 'localhost' || host.endsWith('.loc')) {
                return window.location.origin
            }
        }

        const env = config.public.APP_ENV
        if (import.meta.dev || env === 'local' || env === 'dev') {
            return import.meta.client ? window.location.origin : 'http://localhost:6001'
        }

        return 'https://pimway.se:6001'
    }

    function socketOptions() {
        const url = socketUrl()

        return {
            path: '/socket.io',
            transports: ['websocket', 'polling'],
            withCredentials: false,
            secure: url.startsWith('https'),
        }
    }

    function isTempId(id) {
        return typeof id === 'string' && id.startsWith('tmp-')
    }

    function upsertMappedMessage(mapped) {
        if (!mapped) {
            return false
        }

        const existing = messages.value.filter((item) => item.id !== 'greeting')
        if (existing.some((item) => item.id === mapped.id)) {
            return false
        }

        let replacedTemp = false
        const next = existing.map((item) => {
            if (
                !replacedTemp
                && isTempId(item.id)
                && item.role === mapped.role
                && item.text === mapped.text
            ) {
                replacedTemp = true
                return mapped
            }

            return item
        }).filter((item, index, list) => list.findIndex((other) => other.id === item.id) === index)

        if (!replacedTemp) {
            next.push(mapped)
        }

        messages.value = next
        return true
    }

    function appendIncoming(payload) {
        const incoming = payload?.message || payload?.data?.message
        const chat = payload?.chat || payload?.data?.chat
        if (!incoming) {
            return
        }

        if (chat?.token && joinedToken && chat.token !== joinedToken) {
            return
        }

        const mapped = mapMessage(incoming)
        const added = upsertMappedMessage(mapped)
        applyCartCommands(mapped)

        if (!added) {
            return
        }

        if (mapped.role === 'agent' || mapped.role === 'system') {
            setAwaitingReply(false)
        }

        if (!isOpen.value && mapped.role !== 'user') {
            unreadCount.value += 1
        }
    }

    async function loadIo() {
        if (!loadIoPromise) {
            loadIoPromise = import('socket.io-client').then((mod) => mod.io || mod.default)
        }

        return loadIoPromise
    }

    function bindMessageEvent(token) {
        if (!visitorSocket || !token) {
            return
        }

        messageListener = appendIncoming

        if (boundEvent) {
            visitorSocket.off(boundEvent, messageListener)
            visitorSocket.off('message', messageListener)
        }

        boundEvent = `website-chat.${token}:message`
        joinedToken = token
        visitorSocket.on(boundEvent, messageListener)
        visitorSocket.on('message', messageListener)
        visitorSocket.emit('join', token)
    }

    async function connectVisitorSocket(token) {
        if (!import.meta.client || !token) {
            return
        }

        const io = await loadIo()

        if (!visitorSocket) {
            visitorSocket = io(socketUrl() + '/website-chat', {
                ...socketOptions(),
                auth: {token},
                query: {token},
            })
            visitorSocket.on('connect', onSocketConnect)
        } else if (!visitorSocket.connected) {
            visitorSocket.auth = {token}
            visitorSocket.connect()
        }

        bindMessageEvent(token)
    }

    function onSocketConnect() {
        if (joinedToken) {
            bindMessageEvent(joinedToken)
        }
    }

    function disconnectVisitorSocket() {
        if (!visitorSocket) {
            return
        }

        if (boundEvent) {
            visitorSocket.off(boundEvent, messageListener)
        }

        visitorSocket.off('message', messageListener)
        visitorSocket.off('connect', onSocketConnect)
        visitorSocket.disconnect()
        visitorSocket = null
        joinedToken = null
        messageListener = null
        boundEvent = null
    }

    async function loadHistory() {
        if (loaded.value) {
            return
        }

        loaded.value = true

        if (!tokenCookie.value) {
            messages.value = [greetingMessage()]
            return
        }

        try {
            const data = await useClientFetch(`chat/${tokenCookie.value}`)
            const history = Array.isArray(data?.messages)
                ? data.messages.map(mapMessage).filter((item) => item.text)
                : []
            messages.value = history.length ? history : [greetingMessage()]
            history.forEach(applyCartCommands)
            refreshLiveSync()
        } catch {
            tokenCookie.value = null
            messages.value = [greetingMessage()]
            return
        }

        try {
            await connectVisitorSocket(tokenCookie.value)
        } catch {
            // History is already shown; live updates can retry on the next message.
        }
    }

    function stopPolling() {
        if (pollTimer) {
            clearInterval(pollTimer)
            pollTimer = null
        }
    }

    async function syncFromServer() {
        const token = tokenCookie.value
        if (!token) {
            return
        }

        try {
            const data = await useClientFetch(`chat/${token}`, {
                query: {_: Date.now()},
            })
            const history = Array.isArray(data?.messages)
                ? data.messages.map(mapMessage).filter((item) => item.text)
                : []
            if (!history.length) {
                return
            }

            const current = messages.value.filter((item) => item.id !== 'greeting')
            const unchanged = current.length === history.length
                && current.every((item, index) => {
                    const next = history[index]
                    return next
                        && item.id === next.id
                        && item.text === next.text
                        && item.role === next.role
                })

            if (unchanged) {
                const last = history[history.length - 1]
                if (last && (last.role === 'agent' || last.role === 'system')) {
                    setAwaitingReply(false)
                }
                return
            }

            messages.value = history
            history.forEach(applyCartCommands)

            const last = history[history.length - 1]
            if (last && (last.role === 'agent' || last.role === 'system')) {
                setAwaitingReply(false)
            }
        } catch {
            // Socket or the next poll can still pick the reply up.
        }
    }

    function startPolling(interval) {
        stopPolling()
        pollTimer = setInterval(syncFromServer, interval)
    }

    function refreshLiveSync() {
        if (!import.meta.client || !tokenCookie.value) {
            stopPolling()
            return
        }

        if (awaitingReply.value) {
            startPolling(1500)
            return
        }

        if (isOpen.value) {
            startPolling(3000)
            return
        }

        stopPolling()
    }

    function setAwaitingReply(value) {
        awaitingReply.value = value

        if (awaitingTimer) {
            clearTimeout(awaitingTimer)
            awaitingTimer = null
        }

        if (!value) {
            refreshLiveSync()
            return
        }

        syncFromServer()
        startPolling(1500)

        awaitingTimer = setTimeout(() => {
            awaitingReply.value = false
            awaitingTimer = null
            refreshLiveSync()
        }, 45000)
    }

    function replaceTempMessage(tempId, mapped) {
        if (upsertMappedMessage(mapped)) {
            messages.value = messages.value.filter((item) => item.id !== tempId)
            return
        }

        messages.value = messages.value.filter((item) => item.id !== tempId && item.id !== 'greeting')
    }

    async function sendMessage(text) {
        const value = String(text || '').trim()
        if (!value || sending.value) {
            return
        }

        sending.value = true

        const tempId = `tmp-${Date.now()}`
        messages.value = [...messages.value.filter((item) => item.id !== 'greeting'), {
            id: tempId,
            role: 'user',
            text: value,
            time: formatTime(new Date()),
        }]

        try {
            const body = {text: value}
            if (tokenCookie.value) {
                body.token = tokenCookie.value
            }

            const data = await useClientFetch('chat/messages', {
                method: 'POST',
                body,
            })

            setToken(data.token)

            if (data.message) {
                replaceTempMessage(tempId, mapMessage(data.message))
            }

            setAwaitingReply(Boolean(data.ai_pending))
        } catch (error) {
            setAwaitingReply(false)
            messages.value = messages.value.filter((message) => message.id !== tempId)
            throw error
        } finally {
            sending.value = false
        }

        if (tokenCookie.value) {
            try {
                await connectVisitorSocket(tokenCookie.value)
            } catch {
                // Message already saved; live updates can retry on the next send.
            }
        }
    }

    function open() {
        isOpen.value = true
        unreadCount.value = 0
    }

    function close() {
        isOpen.value = false
    }

    function toggle() {
        if (isOpen.value) {
            close()
        } else {
            open()
        }
    }

    if (import.meta.client) {
        watch(tokenCookie, (token) => {
            if (token) {
                connectVisitorSocket(token)
                refreshLiveSync()
            }
        })

        watch(isOpen, (open) => {
            if (open) {
                syncFromServer()
            }
            refreshLiveSync()
        })

        onMounted(() => {
            if (tokenCookie.value) {
                connectVisitorSocket(tokenCookie.value)
            }
            refreshLiveSync()
        })
    }

    return {
        isOpen,
        unreadCount,
        messages,
        sending,
        awaitingReply,
        token: tokenCookie,
        loadHistory,
        sendMessage,
        open,
        close,
        toggle,
    }
}
